import { useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { NavLink } from "react-router-dom";
import { Moon, Sun, Monitor, X } from "lucide-react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { BrandLogo } from "./BrandLogo";
import { useThemeStore, type ThemeMode } from "../../lib/stores/theme";

const APPEARANCE_OPTIONS: { value: ThemeMode; label: string; icon: typeof Sun }[] = [
  { value: "system", label: "System", icon: Monitor },
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon }
];

function ThemeRow() {
  const { mode, setMode } = useThemeStore();

  return (
    <div className="py-3">
      <p className="text-sm font-medium text-ink-muted mb-2.5">Appearance</p>
      <div className="grid grid-cols-3 gap-1.5 rounded-xl bg-surface p-1">
        {APPEARANCE_OPTIONS.map(({ value, label, icon: Icon }) => {
          const active = mode === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={active}
              className={clsx(
                "flex flex-col items-center justify-center gap-1 rounded-lg py-2.5 text-xs font-medium transition-colors duration-300 ease-physical active:scale-95",
                active ? "bg-accent text-ink" : "text-ink-muted hover:text-ink"
              )}
            >
              <Icon size={16} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export interface MobileNavLink {
  to: string;
  label: string;
}

export interface MobileNavProps {
  open: boolean;
  onClose: () => void;
  links: MobileNavLink[];
}

export function MobileNav({ open, onClose, links }: MobileNavProps) {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 lg:hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
        >
          <div className="absolute inset-0 bg-black/40" onClick={onClose} />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-label="Site navigation"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-0 top-0 h-full w-full max-w-xs bg-surface-raised border-l border-border flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-16 border-b border-border shrink-0">
              <BrandLogo variant="icon" className="h-8 w-auto" />
              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                aria-label="Close menu"
                className="h-11 w-11 rounded-full flex items-center justify-center text-ink-muted hover:text-ink hover:bg-surface transition-colors duration-300 ease-physical active:scale-95"
              >
                <X size={20} />
              </button>
            </div>
            <nav className="flex-1 overflow-y-auto px-6 py-6 flex flex-col gap-1">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  onClick={onClose}
                  className={({ isActive }) =>
                    `py-3 text-lg font-medium tracking-tight transition-colors duration-300 ease-physical ${
                      isActive ? "text-accent" : "text-ink hover:text-accent"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
              <div className="mt-2 pt-2 border-t border-border">
                <ThemeRow />
              </div>
            </nav>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
